/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useMemo, useRef, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks";
import {
  Object as ArgosObject,
  selectEditSensorWizard,
  selectCurrentStudyId,
  selectStudy,
  setEditSensorWizardData,
  setEditSensorWizardObjectsData,
  setEditSensorWizardSelectedObjectIds
} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign";
import {Link} from "@mui/material";
import {MRT_ColumnDef, MRT_RowSelectionState} from "material-react-table";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import {ArgosOpenInNewTabIcon} from "../../../../../components/Icons/ArgosActionIcons";
import {usePostRequest} from "../../../../../../api/helper";
import ADAS_PROGRAMS_CONSTANTS from "../../../../../components/Tree/constants";
import toast from "react-hot-toast";
import {selectProgram} from "../../../../../../redux/ducks/AdasPrograms";
import {cloneDeep} from "lodash";
import {selectConfigSensorWizardSelectSensorStep, selectSensorBomData} from "../../../../../../redux/ducks/AdasPrograms/slice";
import {EAdasProgramSensorBomCheckboxTypes} from "../../../../AdasPrograms/ItemDetails/types";
import {getObjectDisplayName} from "../../../../CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties";
import {useDeepCompareEffect} from "../../../../../../hooks/useDeepCompareEffect/useDeepCompareEffect";
import {ArgosTableSimple} from "../../../../../components/Table/ArgosTableSimple";


/** Step name to display in wizard */
const REVIEW_OBJECTS_STEP_NAME = 'Review objects'

const COLUMNS: MRT_ColumnDef<ArgosObject>[] = [
  {
    accessorKey: 'type',
    header: 'Object Category',
    filterVariant: 'multi-select',
  },
  {
    accessorKey: 'name',
    header: 'Object Name',
    Cell: ({cell}) => {
      const objectId = cell.row.original.id
      const url = `/objectDetails?htabs-1=0&htabs-1-name=Object%20Details&id=${objectId}`
      return (
        <Link
          href={url} underline='hover'
          onClick={(event) => {
            event.preventDefault()
            window.open(url, '_blank')
          }}
          style={{display: 'flex', alignItems: 'center'}}
        >
          {cell.getValue<string>()}
          <ArgosOpenInNewTabIcon style={{paddingLeft: '5px', fontSize: '18px'}}/>
        </Link>
      )
    },
  },
  {
    id: 'pixelsAcrossShortDimension',
    header: 'Pixels Across Short Dimension',
    filterVariant: 'range',
    filterFn: 'between',
  },
  {
    accessorKey: 'comments',
    header: 'Comments',
  }
]

const PIXELS_ACROSS_DIMENSION_DEFAULT = 20

/** Allows the user to review the objects of the chosen ADAS Program */
export const ReviewObjectsStep = React.memo(() => {
  const dispatch = useAppDispatch()

  const formData = useAppSelector(selectEditSensorWizard).data
  const isFetching = formData?.objects?.isFetching ?? false

  const dataSetsData = formData?.dataSets?.data ?? {}
  const selectedDataSet = formData?.dataSets?.selectedDataSet ?? ''

  const currentStudyId = useAppSelector(selectCurrentStudyId);
  const study = useAppSelector((state) => selectStudy(state, currentStudyId ?? ""));
  const adasProgram = useAppSelector((state) => selectProgram(state, study?.adasProgramId ?? ""));
  const adasProgramId = adasProgram?.id;

  const selectSensorStepState = useAppSelector(selectConfigSensorWizardSelectSensorStep)

  /**
   * Get existing objects for this sensor
   */
  const objectsData = useAppSelector(state => {
    const sensorId = selectSensorStepState?.id
    let locationKey = selectSensorStepState?.locationKey

    if (typeof locationKey === 'string') {
      locationKey = [locationKey]
    }

    if (sensorId && locationKey) {
      const sensorData = selectSensorBomData(state)?.data?.sensors?.[sensorId]?.sensorLocations?.filter((loc: any) =>
        loc.locationKey === locationKey?.[0]
      )[0]
      const objects = sensorData?.objects
      if (objects) {
        return objects
      }
    }
    return undefined
  })
  /**
   * Use the existing objects data as initial state for which objects are selected.
   */
  const previousSelectedObjectIds = objectsData ? Object.keys(objectsData) : []
  useEffect(() => {
    if (previousSelectedObjectIds.length > 0) {
      const rowSelectState = previousSelectedObjectIds.reduce((acc: any, id: string) => {
        acc[id] = true
        return acc
      }, {})
      dispatch(setEditSensorWizardSelectedObjectIds(rowSelectState))
      // TODO: Check if any of these objects exist in formData.objects.data.
      //  If so, overwrite with this data. If not, can add them although probably network call will override them so be sure
      //  it doesn't then you can add here. We want to add here because if somehow this happens after the network call to fetch all objects we need
      //  to override it with this data.
      dispatch(setEditSensorWizardObjectsData({type: 'addOrOverride', data: objectsData}))
    }
  }, [objectsData]);

  const fetchObjectsFromRedlineDatasetHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchObjectsFromRedlineDatasetEndpoint)
  const [isLoadingObjects, setIsLoadingObjects] = useState(false)
  const [objectsForTable, setObjectsForTable] = useState<ArgosObject[]>([])
  /**
   * Fetch objects from redline dataset and store in different forms both in useState and in redux
   */
  const fetchObjectsFromRedlineDataset = () => {
    if (!selectedDataSet) return
    setIsLoadingObjects(true)
    fetchObjectsFromRedlineDatasetHandle({datasetId: selectedDataSet,studyId:currentStudyId}).then((_objects) => {
      if (!_objects) {
        toast.error('No objects found')
        return
      }
      /**
       * _objects is map of objId to db item of object from Objects container
       */


      /**
       * We only want to select a few fields an object to store in greenline state.
       */

      let objects: any = {}

      // Create a map of object id to data
      for (const objectId in _objects) {
        const object = _objects[objectId]
        const objectCategory = object.category
        const objectName = object.name
        const variantName = object.variantName
        const objectDisplayName = getObjectDisplayName(objectCategory, objectName, variantName)
        objects[object.id] = {
          id: object.id,
          type: object.category,
          name: objectDisplayName,
          pixelsAcrossShortDimension: object.pixelsAcrossShortDimension ?? PIXELS_ACROSS_DIMENSION_DEFAULT,
          comments: object.comments ?? ''
        }
      }

      // Need to add objectsData here because it gives the previously set values for things like pixelsAcrossShortDimension.
      // Make deep copy of objectsData in case there are nested properties
      // if(!isInitialSet.current) { // TODO: This causes this not to run ever. But if we always run this, then will the pixelsAcrossShortDimension get overridden with whats in location even after changing the dataset? Yes.
      const clonedObjectsData = cloneDeep(objectsData)
      for (const objectId in clonedObjectsData) {
        if (objects[objectId]) {
          objects[objectId] = {...objects[objectId], ...clonedObjectsData[objectId]};
        }
      }

      // set connectAdasProgramWizardData
      // TODO: Test which of these should be used.
      // dispatch(setEditSensorWizardObjectsData({type: 'onlyAddNew', data: objects}))
      dispatch(setEditSensorWizardObjectsData({type: 'replace', data: objects}))

    }).catch((err) => {
      toast.error('Error fetching objects from redline dataset')
      console.error(err)
    }).finally(() => {
      setIsLoadingObjects(false)
    })
  }

  /**
   * Update the table state when redux changes
   */
  useEffect(() => {
    const _objectsForTable: any[] = cloneDeep(formData?.objects?.data ? Object.values(formData?.objects?.data) : [])
    setObjectsForTable(_objectsForTable)
  }, [formData]);


  /**
   * Reset the objects state
   */
  const resetObjectsState = () => {
    setObjectsForTable([])
    dispatch(setEditSensorWizardObjectsData({type: 'reset'}))
    dispatch(setEditSensorWizardSelectedObjectIds({}));
  }

  const [isResetComplete, setIsResetComplete] = useState(false);
  const isInitialSet = useRef(false);
  const hasFetchedInitially = useRef(false);

  /**
   * NOTE: We are using useDeepCompareEffect here because selectedDataSet is an array and we dont want this to run if
   * the array reference changes but the contents are the same.
   */
  useDeepCompareEffect(() => {
    if (selectedDataSet) {
      if (!isInitialSet.current) {
        isInitialSet.current = true;
        if (!hasFetchedInitially.current) {
          fetchObjectsFromRedlineDataset();
          hasFetchedInitially.current = true;
        }
      } else {
        resetObjectsState();
        setIsResetComplete(true);
      }
    }
  }, [selectedDataSet]);
  /**
   * Fetch objects from redline dataset when selectedDataSet (from previous wizard step) changes (these objects are what display in table)
   */
  useEffect(() => {
    if (isResetComplete && selectedDataSet && adasProgramId) {
      fetchObjectsFromRedlineDataset();
      setIsResetComplete(false);
    }
  }, [isResetComplete, selectedDataSet, adasProgramId, objectsData]);


  const editedTableData = useRef<any>()

  /**
   * Column visibility state passed into table component. If col not in state, defaults to true (visible).
   */
  const [columnVisibility, setColumnVisibility] = useState({
    pixelsAcrossShortDimension: false,
  });

  /**
   * If the type of sensor is not Cameras then hide the pixelsAcrossShortDimension column
   */
  const sensorStepState = useAppSelector(selectConfigSensorWizardSelectSensorStep)
  useEffect(() => {
    const isCamera = sensorStepState?.modality === EAdasProgramSensorBomCheckboxTypes.CAMERAS
    setColumnVisibility({pixelsAcrossShortDimension: isCamera});
  }, [sensorStepState]);

  const columns = useMemo<MRT_ColumnDef<ArgosObject>[]>(() => {
    return COLUMNS.map(column => {
      if (column.id === 'pixelsAcrossShortDimension') {
        return {
          ...column,
          /**
           * TODO:
           * In ADAS Programs, why are values for some objects stored as strings
           * and stored as numbers for others? Converting all values to number
           * to make use of table filters.
           */
          accessorFn: (originalRow) => Number(originalRow.pixelsAcrossShortDimension) ?? '',
          enableEditing: true,
          muiEditTextFieldProps: ({cell, row}) => ({
            type: 'number',
            required: true,
            onBlur: (event) => {
              editedTableData.current = {
                ...editedTableData.current,
                [row.id]: {...row.original, pixelsAcrossShortDimension: Number(event.target.value)}
              }
              // dispatch to save edited data
              const previousFormData = cloneDeep(formData);
              previousFormData.objects.data[row.id].pixelsAcrossShortDimension = Number(event.target.value);
              dispatch(setEditSensorWizardData(previousFormData));
            },
          }),
        }
      }
      return {...column, enableEditing: false}
    })
  }, [formData])

  /**
   * State management for row selection (selectedObjectIds are stored in redux)
   */
  const selectedObjectIds: MRT_RowSelectionState = useMemo<MRT_RowSelectionState>(() => {
    if (formData?.objects?.selectedObjectIds)
      return formData?.objects?.selectedObjectIds
    else
      return {}
  }, [formData])
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(selectedObjectIds);
  useEffect(() => {
    // Using this state because otherwise typescript error.
    setRowSelection(selectedObjectIds);
  }, [selectedObjectIds]);

  /**
   * Update redux state when row selection changes
   * @param newRowSelection updated row selection state (all current selected rows) in format {id: boolean}
   */
  const handleRowSelectionChange = (newRowSelection: any) => {
    // selectedObjectIds is {id: boolean}. newRowSelection is the updated state. update redux here, which will then update the rowSelection state.
    dispatch(setEditSensorWizardSelectedObjectIds(newRowSelection));
  };

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%', gap: 20, padding: '10px'}}>
      <ArgosTableSimple
        data={objectsForTable}
        columns={columns}

        /** State management */
        layoutMode='semantic'
        getRowId={(row) => row.id}
        state={{
          showSkeletons: isFetching,
          showLoadingOverlay: isLoadingObjects,
          rowSelection,
          columnVisibility
        }}

        enableRowSelection={true}
        onRowSelectionChange={(updater: any) => {
          const prev = selectedObjectIds;
          const newState = updater(prev);
          handleRowSelectionChange(newState);
        }}

        editDisplayMode="cell"
        enableEditing={true}

        /** Toolbar */
        enableFacetedValues
        enableBottomToolbar={false}
      />
    </ArgosFlexbox>
  )
})
